import { gql } from '@apollo/client';
import { Address, IProject } from 'spw-redux/modules/projects/types';
import {
  BenchmarkProject,
  BenchmarkReport,
  BenchmarkReportEditor,
  BenchmarkReportSummary,
  DismissedIssuesMap,
  GetBenchmarkReportsParams,
  MissingData,
  MissingProjectData,
  PaginatedBenchmarkReports,
} from 'spw-redux/modules/benchmark-reports/types';
import { ClientSlimForList } from 'spw-redux/modules/clients/types';
import { graphqlClient } from './utils';

export const getLocationsForBenchmarkProjectsApi = () => {
  return graphqlClient.query<{
    locations: Address[];
  }>({
    fetchPolicy: 'no-cache',
    query: gql`
      query GetLocationsForBenchmarkProjects {
        locations: getLocationsForBenchmarkProjects {
          city
          state
          countryCode
        }
      }
    `,
  });
};

export const getUSFRangeOfBenchmarkProjectsApi = () => {
  return graphqlClient.query<{
    range: { min: number; max: number };
  }>({
    fetchPolicy: 'no-cache',
    query: gql`
      query GetUSFRangeOfBenchmarkProjects {
        range: getUSFRangeOfBenchmarkProjects {
          min
          max
        }
      }
    `,
  });
};

export const getMissingProjectDataApi = (projectId: number) => {
  return graphqlClient.query<{
    missingProjectData: MissingProjectData;
  }>({
    fetchPolicy: 'no-cache',
    query: gql`
      query getMissingData($projectId: Int!) {
        missingProjectData: getMissingProjectData(projectId: $projectId) {
          iwSpacesMissingUsf
          iwSpacesMissingSeatcount
          meetingSpacesMissingUsf
          meetingSpacesMissingSeatcount
          amenitySpacesMissingUsf
          amenitySpacesMissingSeatcount
          supportSpacesMissingUsf
          floorsMissingTotalUsf
          headcount
          location
          floorsWithNoFloorplansCount
          floorsWithNoSpacesCount
          hasNoFloorplans
        }
      }
    `,
    variables: {
      projectId,
    },
  });
};

export const getMissingDataApi = (projectId: number) => {
  return graphqlClient.query<{
    missingProjectData: MissingData;
  }>({
    fetchPolicy: 'no-cache',
    query: gql`
      query getMissingData($projectId: Int!) {
        missingProjectData: missingProjectData(projectId: $projectId) {
          projectId
          missingFields {
            field
            isResolved
          }
          missingBuildingData {
            buildingId
            missingFloorData {
              floorId
              missingFields {
                id
                field
                fieldId
                quantity
                isResolved
              }
            }
          }
        }
      }
    `,
    variables: {
      projectId,
    },
  });
};

export const dismissBenchmarkIssuesApi = (
  projectId: number,
  issuesToDismiss: DismissedIssuesMap
) => {
  return graphqlClient.query<{
    missingProjectData: MissingProjectData;
  }>({
    fetchPolicy: 'no-cache',
    query: gql`
      mutation DismissBenchmarkIssues(
        $projectId: Int!
        $issuesToDismiss: DismissableBenchmarkIssues
      ) {
        missingProjectData: dismissBenchmarkIssues(
          projectId: $projectId
          issuesToDismiss: $issuesToDismiss
        ) {
          projectId
          individualWorkSpaceUsf
          individualWorkSeats
          meetingSpaceUsf
          meetingSeats
          amenitySpaceUsf
          amenitySeats
          supportSpaceUsf
          totalUsfPerFloor
          location
          headcount
          floorsWithNoFloorplansCount
          floorsWithNoSpacesCount
          hasNoFloorplans
        }
      }
    `,
    variables: {
      projectId,
      issuesToDismiss,
    },
  });
};

export const getBenchmarkReportPreviewApi = (projectIds: number[]) => {
  return graphqlClient.query<{
    summary: BenchmarkReportSummary;
  }>({
    fetchPolicy: 'no-cache',
    query: gql`
      query GetBenchmarkReportPreview($args: ReportPreviewArgs!) {
        summary: benchmarkReportPreview(args: $args) {
          averageProjectSize
          industries
          individualWorkspacesAnalysis {
            averageSquareFeet
            name
            seatMix
            subcategory
          }
          linkedProjects
          usfAllocationBySpaceCategory {
            category
            usfAllocationPercent
          }
          usfMetrics {
            usfPerHeadcount
            usfPerWorkSeat
          }
          workSeatRatios {
            meetingSeatsRatio
            sharedSeatsRatio
            headcountRatio
          }
        }
      }
    `,
    variables: {
      args: {
        projectIds,
      },
    },
  });
};

export const getBenchmarkProjectsByIdApi = (projectIds: number[]) => {
  return graphqlClient.query<{
    projects: { projects: IProject[]; total: number };
  }>({
    fetchPolicy: 'no-cache',
    query: gql`
      query GetSelectedBenchmarkProjects(
        $projectIds: [Int]
        $perPage: Int
        $page: Int
      ) {
        projects(perPage: $perPage, page: $page, projectIds: $projectIds) {
          projects {
            id
            isMember
            insertedAt
            updatedAt
            client {
              name
              id
              industry {
                name
              }
            }
            city
            state
            countryCode
            projectName
            projectNumber
            requiresRequestToJoin
            isMarkedAsFavoriteByUser
            status
            year
            lastEditor {
              id
              name
            }
            createdByUser {
              id
              name
              email
            }
            projectOwner {
              id
              name
              email
              isActive
            }
            type
          }
          total
        }
      }
    `,
    variables: { projectIds, page: 1, perPage: 1000 },
  });
};

export const getClientsOfBenchmarkProjectsApi = () =>
  graphqlClient.query<{ clients: ClientSlimForList[] }>({
    fetchPolicy: 'no-cache',
    query: gql`
      query GetClientsOfBenchmarkProjects {
        clients: getClientsOfBenchmarkProjects {
          id
          name
          industry {
            id
            name
          }
          projects {
            id
            archived
          }
          numberOfTeams
        }
      }
    `,
  });

export const createBenchmarkReportApi = (report: BenchmarkReport) =>
  graphqlClient.query<{ report: BenchmarkReport }>({
    fetchPolicy: 'no-cache',
    query: gql`
      mutation CreateBenchmarkReport($args: CreateReportArgs!) {
        report: createBenchmarkReport(args: $args) {
          id
          name
          summary {
            averageProjectSize
            industries
            individualWorkspacesAnalysis {
              averageSquareFeet
              name
              seatMix
              subcategory
            }
            linkedProjects
            usfAllocationBySpaceCategory {
              category
              usfAllocationPercent
            }
            usfMetrics {
              usfPerHeadcount
              usfPerWorkSeat
            }
            workSeatRatios {
              meetingSeatsRatio
              sharedSeatsRatio
              headcountRatio
            }
          }
        }
      }
    `,
    variables: {
      args: report,
    },
  });

export const getBenchmarkReportApi = (reportId: number) => {
  return graphqlClient.query<{
    report: BenchmarkReport;
  }>({
    fetchPolicy: 'no-cache',
    query: gql`
      query GetBenchmarkReport($id: Int) {
        report: benchmarkReport(id: $id) {
          id
          name
          verified
          approved
          isMarkedAsFavoriteByUser
          categories {
            id
            name
            color
            gradientColorIndex
            defaultCategory
            parentId
          }
          icons {
            id
            cardType
            iconType
            iconPath
          }
          insertedAt
          updatedAt
          organizationId
          owner {
            id
            name
            isActive
            hasAvatar
            thumbnailS3Path
            officeLocation
            professionalRole
            isSuperAdmin
          }
          verifier {
            id
            name
            isActive
            hasAvatar
            thumbnailS3Path
          }
          lastEditor {
            id
            name
            isActive
            hasAvatar
            thumbnailS3Path
          }
          creator {
            id
            name
            isActive
            hasAvatar
            thumbnailS3Path
          }
          lastVerifiedAt
          benchmarkProjects {
            linked
            forciblyUnlinked
            projectId
            project {
              id
              insertedAt
              updatedAt
              client {
                name
                id
                industry {
                  name
                }
              }
              city
              state
              countryCode
              projectName
              projectNumber
              status
              year
            }
          }
          currentUserRole
          isMember
        }
      }
    `,
    variables: {
      id: reportId,
    },
  });
};

export const getBenchmarkReportsApi = (params: GetBenchmarkReportsParams) => {
  return graphqlClient.query<{
    paginatedReports: PaginatedBenchmarkReports;
  }>({
    fetchPolicy: 'no-cache',
    query: gql`
      query GetBenchmarkReports(
        $page: Int
        $perPage: Int
        $sort: BenchmarkReportSortOptions
        $filter: BenchmarkReportsFilter
      ) {
        paginatedReports: benchmarkReports(
          perPage: $perPage
          page: $page
          sort: $sort
          filter: $filter
        ) {
          total
          reports: benchmarkReports {
            id
            name
            approved
            verified
            isMarkedAsFavoriteByUser
            linkedProjectsCount
            industries
            owner {
              id
              name
              isActive
            }
            lastVerifiedAt
            verifier {
              id
              name
            }
            lastEditor {
              id
              name
              isSuperAdmin
            }
            summary {
              industries
              linkedProjects
            }
            benchmarkProjects {
              linked
              projectId
              project {
                id
                projectName
                projectNumber
              }
            }
            insertedAt
            updatedAt
            currentUserRole
            isMember
          }
        }
      }
    `,
    variables: params,
  });
};

export const validateBenchmarkReportNameApi = (name: string) => {
  return graphqlClient.query<{
    validation: { nameIsTaken: boolean };
  }>({
    fetchPolicy: 'no-cache',
    query: gql`
      query ValidateBenchmarkReportName($name: String!) {
        validation: validateBenchmarkReportName(name: $name) {
          nameIsTaken
        }
      }
    `,
    variables: { name },
  });
};

export const addReportToFavoritesApi = (reportId: number) => {
  return graphqlClient.query<{
    favoriteReport: { id: number };
  }>({
    fetchPolicy: 'no-cache',
    query: gql`
      mutation AddReportToFavorites($reportId: Int!) {
        userFavoriteBenchmarkReport: addReportToFavorites(reportId: $reportId) {
          id
        }
      }
    `,
    variables: { reportId },
  });
};

export const removeReportFromFavoritesApi = (reportId: number) => {
  return graphqlClient.query<{
    favoriteReport: { id: number };
  }>({
    fetchPolicy: 'no-cache',
    query: gql`
      mutation RemoveReportFromFavorites($reportId: Int!) {
        userFavoriteBenchmarkReport: removeReportFromFavorites(
          reportId: $reportId
        ) {
          id
        }
      }
    `,
    variables: { reportId },
  });
};

export const editBenchmarkReportApi = (report: Partial<BenchmarkReport>) => {
  return graphqlClient.query<{
    editedReport: BenchmarkReport;
  }>({
    fetchPolicy: 'no-cache',
    query: gql`
      mutation EditBenchmarkReport($report: EditBenchmarkReportArgs) {
        editedReport: editBenchmarkReport(report: $report) {
          id
          name
          insertedAt
          updatedAt
        }
      }
    `,
    variables: { report },
  });
};

export const deleteBenchmarkReportApi = (reportId: number) => {
  return graphqlClient.query<{
    deletedReport: BenchmarkReport;
  }>({
    fetchPolicy: 'no-cache',
    query: gql`
      mutation DeleteBenchmarkReport($reportId: Int!) {
        deletedReport: deleteBenchmarkReport(reportId: $reportId) {
          id
          name
          insertedAt
          updatedAt
        }
      }
    `,
    variables: { reportId },
  });
};

export const verifyBenchmarkReportApi = (reportId: number) => {
  return graphqlClient.query<{
    report: BenchmarkReport;
  }>({
    fetchPolicy: 'no-cache',
    query: gql`
      mutation verifyBenchmarkReport($reportId: ID!) {
        report: verifyBenchmarkReport(reportId: $reportId) {
          id
          name
          verified
          approved
          verifier {
            id
            name
            isActive
            hasAvatar
            thumbnailS3Path
          }
          lastVerifiedAt
          insertedAt
          updatedAt
        }
      }
    `,
    variables: { reportId },
  });
};

export const updateBenchmarkReportProjectsApi = (
  reportId: number,
  projects: BenchmarkProject[]
) => {
  return graphqlClient.query<{
    benchmarkProjects: BenchmarkProject[];
  }>({
    fetchPolicy: 'no-cache',
    query: gql`
      mutation updateBenchmarkReportProjects(
        $reportId: Int
        $projects: [BenchmarkProjectArgs]
      ) {
        benchmarkProjects: updateBenchmarkProjects(
          reportId: $reportId
          projects: $projects
        ) {
          linked
          projectId
          forciblyUnlinked
        }
      }
    `,
    variables: { reportId, projects },
  });
};

export const addBenchmarkReportEditorsApi = (params: {
  reportId: number;
  usersIds: number[];
}) => {
  return graphqlClient.query<{
    reportEditors: BenchmarkReportEditor[];
  }>({
    fetchPolicy: 'no-cache',
    query: gql`
      mutation addBenchmarkReportEditors($reportId: Int, $usersIds: [Int]) {
        reportEditors: addBenchmarkReportEditors(
          reportId: $reportId
          usersIds: $usersIds
        ) {
          id
          user {
            id
            name
            isActive
            hasAvatar
            thumbnailS3Path
            professionalRole
            officeLocation
          }
        }
      }
    `,
    variables: params,
  });
};

export const getBenchmarkReportEditorsApi = (reportId: number) => {
  return graphqlClient.query<{
    reportEditors: BenchmarkReportEditor[];
  }>({
    fetchPolicy: 'no-cache',
    query: gql`
      query GetBenchmarkReportEditors($reportId: Int) {
        reportEditors: getBenchmarkReportEditors(reportId: $reportId) {
          id
          user {
            id
            name
            isActive
            hasAvatar
            thumbnailS3Path
            professionalRole
            officeLocation
          }
        }
      }
    `,
    variables: { reportId },
  });
};

export const removeBenchmarkReportEditorApi = (params: {
  reportId: number;
  userId: number;
}) => {
  return graphqlClient.query<{
    reportEditors: BenchmarkReportEditor[];
  }>({
    fetchPolicy: 'no-cache',
    query: gql`
      mutation removeBenchmarkReportEditor($reportId: Int, $userId: Int) {
        reportEditors: removeBenchmarkReportEditor(
          reportId: $reportId
          userId: $userId
        ) {
          id
          user {
            id
            name
            isActive
            hasAvatar
            thumbnailS3Path
            professionalRole
            officeLocation
          }
        }
      }
    `,
    variables: params,
  });
};

export const transferBenchmarkReportOwnershipApi = (params: {
  reportId: number;
  newOwnerId: number;
  shouldKeepOwnerAsEditor: boolean;
}) => {
  return graphqlClient.query<{
    report: BenchmarkReport;
  }>({
    fetchPolicy: 'no-cache',
    query: gql`
      mutation transferBenchmarkReportOwnership(
        $reportId: Int
        $newOwnerId: Int
        $shouldKeepOwnerAsEditor: Boolean
      ) {
        report: transferBenchmarkReportOwnership(
          reportId: $reportId
          newOwnerId: $newOwnerId
          shouldKeepOwnerAsEditor: $shouldKeepOwnerAsEditor
        ) {
          id
          name
          verified
          approved
          isMarkedAsFavoriteByUser
          insertedAt
          updatedAt
          owner {
            id
            name
            isActive
            hasAvatar
            thumbnailS3Path
          }
          verifier {
            id
            name
            isActive
            hasAvatar
            thumbnailS3Path
          }
          lastVerifiedAt
          benchmarkProjects {
            linked
            forciblyUnlinked
            projectId
            project {
              id
              insertedAt
              updatedAt
              client {
                name
                id
                industry {
                  name
                }
              }
              city
              state
              countryCode
              projectName
              projectNumber
              status
              year
            }
          }
          currentUserRole
          isMember
        }
      }
    `,
    variables: params,
  });
};

export const getProjectBenchmarkReports = (projectId: number) => {
  return graphqlClient.query<{ benchmarkReports: BenchmarkReport[] }>({
    fetchPolicy: 'no-cache',
    query: gql`
      query GetProjectBenchmarkReports($projectId: ID) {
        benchmarkReports: projectBenchmarkReports(projectId: $projectId) {
          id
          name
        }
      }
    `,
    variables: { projectId },
  });
};

export const cloneBenchmarkReportApi = (
  srcReportId: number,
  report: BenchmarkReport
) =>
  graphqlClient.query<{ report: BenchmarkReport }>({
    fetchPolicy: 'no-cache',
    query: gql`
      mutation CloneBenchmarkReport(
        $srcReportId: ID!
        $args: CloneReportArgs!
      ) {
        report: cloneBenchmarkReport(srcReportId: $srcReportId, args: $args) {
          id
          name
        }
      }
    `,
    variables: { srcReportId, args: report },
  });

export const getLinkedBenchmarkProjectsApi = (reportId: number) => {
  return graphqlClient.query<{ linkedBenchmarkProjects: BenchmarkProject[] }>({
    fetchPolicy: 'no-cache',
    query: gql`
      query GetLinkedBenchmarkProjects($reportId: ID!) {
        linkedBenchmarkProjects: getLinkedBenchmarkProjects(
          reportId: $reportId
        ) {
          projectId
          linked
          project {
            id
            projectName
            projectNumber
          }
        }
      }
    `,
    variables: { reportId },
  });
};
